/**
 * @module rainbow
 * @description 封装一些常用的七彩石配置拉取接口
 */
import { post } from '@tencent/pmd-network';
import { GoodsConfigInfo } from '@tencent/pmd-config/lib/goods';

/**
 * @description 转换数据格式
 * @param {object} [data={}] 七彩石查询结果
 * @param {string} [dataType='string'] 源数据格式
 * @returns
 */
function dataFormat(data = { value: '' }, dataType = 'string') {
  const { value } = data;

  if (dataType === 'json' && value !== '') {
    return JSON.parse(value);
  }

  return value;
}

/**
 * @export getRainbowConfig
 * @description 基础接口 - 拉取七彩石配置
 * @reference https://git.woa.com/itrpcprotocol/gmp/comm/blob/master/conf/conf.proto
 * @param {object} [reqData={ provider_name: '', path: '', key: '' }]
 * @param {string} reqData.provider_name 配置名称，需要在yaml文件里面指定（若需要新增，需要找后台同学处理）
 * @param {string} reqData.path 具体的项
 * @param {string} reqData.key 配置对应的key值
 * @param {boolean} showMsgToast 返回错误时是否以 toast 形式提示
 * @returns
 */
export function getRainbowConfig(reqData: {
  provider_name: string,
  path: string,
  key?: string,
}, showMsgToast = false) {
  // 赛宝pro这边的cgi只传key值，其他的保持不变
  return new Promise<{value: string, r?: number}>((resolve, reject) => {
    post({
      url: GoodsConfigInfo.instance.getRainbowConfigUrl || '/gmp.comm.conf.conf/GetRainbowConfig',
      reqData: GoodsConfigInfo.instance.getRainbowConfigUrl
        ? { key: reqData.path }
        : reqData,
      showMsgToast,
    }).then((res) => {
      resolve(res);
    })
      .catch((error) => {
        reject();
        console.log('GetRainbowConfig', error);
      });
  });
}

/**
 * @export getFrontConfig
 * @description 拉取 tip.prod.frontconfig 配置分组下的指定配置项(!注意该配置分组不再支持新增配置项，后续请使用 getTipFrontConfig)
 * 七彩石地址：https://rainbow.woa.com/console/2d3cc7f5-8a20-4588-9d94-02c2a562860d/Default/list?group_id=227318&group_name=tip.prod.frontconfig
 * @param {string} [params={ configKey: '', showMsgToast = false }] 请求参数
 * @param {string} params.configKey 配置项的key
 * @param {string} params.dataType 若为 'json' 则会进行 JSON.parse 转换
 * @param {boolean} params.showMsgToast 返回错误时是否以 toast 形式提示
 * @returns {Promise<value>} value 配置项
 */
export function getFrontConfig({ configKey, dataType = 'json', showMsgToast = false }) {
  const reqData = {
    provider_name: 'frontconfig',
    path: configKey,
  };

  return new Promise((resolve, reject) => {
    getRainbowConfig(reqData, showMsgToast)
      .then(res => resolve(dataFormat(res as { value: string }, dataType)))
      .catch((error) => {
        reject();
        console.log('getFrontConfig', error);
      });
  });
}

/**
 * @export getTipFrontConfig
 * @description 拉取  frontconfig.prod  配置分组下的指定配置项，
 * 七彩石地址：http://rainbow.oa.com/console/7fa3bdee-8b2a-4e48-8306-42a8258e660d/Default/list?group_id=3402234&group_name=frontconfig.prod
 * @param {string} [params={ configKey: '', showMsgToast = false }] 请求参数
 * @param {string} params.configKey 配置项的key
 * @param {string} params.dataType 若为 'json' 则会进行 JSON.parse 转换
 * @param {boolean} params.showMsgToast 返回错误时是否以 toast 形式提示
 * @returns {Promise<value>} value 配置项
 */
export function getTipFrontConfig({ configKey, dataType = 'json', showMsgToast = false }) {
  const reqData = {
    provider_name: 'tipfrontconfig',
    path: configKey,
  };

  return getRainbowConfig(reqData, showMsgToast)
    .then(res => dataFormat(res as { value: string }, dataType))
    .catch((error) => {
      console.log('getTipFrontConfig', error);
    });
}
